import { FC } from 'react'

import styled from 'styled-components'
import { ComboboxItem } from '@mantine/core'
import { IconX } from '@tabler/icons-react'

import { HintTooltip } from 'src/Components/HintTooltip'
import { colors } from 'src/Helpers/constants.ts'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { OfferModelUnit } from 'src/Pages/Calculator/useCalculator.tsx'

const UnitContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
  background-color: ${colors.mainGray};
  border-radius: 8px;
  width: 100%;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.thirdBlue};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const PlaceInput = styled(Input)`
  max-width: 190px;
`

const PriceInput = styled(Input)`
  max-width: 150px;
`

const QuantityInput = styled(Input)`
  max-width: 80px;
`

const ToolInput = styled(Select)`
  width: 100%;
  max-width: 600px;
`

interface UnitItemProps {
  unit: OfferModelUnit
  unitIndex: number
  canDelete: boolean
  tools: { label: string; value: string }[]
  onDeparturePointChange: (value: string) => void
  onWorkPointChange: (value: string) => void
  onReturnPointChange: (value: string) => void
  onDeliveryCostChange: (value: number | undefined) => void
  onReturnCostChange: (value: number | undefined) => void
  onQuantityChange: (value: number) => void
  onShiftCountChange: (value: string | null, option: ComboboxItem) => void
  onToolChange: (value: string | null, option: ComboboxItem) => void
  onDelete: () => void
}

export const UnitItem: FC<UnitItemProps> = ({
  unit,
  unitIndex,
  canDelete,
  tools,
  onDeparturePointChange,
  onWorkPointChange,
  onReturnPointChange,
  onDeliveryCostChange,
  onReturnCostChange,
  onQuantityChange,
  onShiftCountChange,
  onToolChange,
  onDelete,
}) => {
  return (
    <UnitContainer data-testid={`unit-item-${unitIndex}`}>
      <FieldWrapper>
        <PlaceInput
          label='Пункт отправки'
          placeholder='Зима'
          value={unit.departurePoint || ''}
          onChange={(e) => onDeparturePointChange(e.target.value)}
          data-testid={`unit-departure-point-${unitIndex}`}
        />
        <PlaceInput
          label='Пункт работ'
          placeholder='Зима'
          value={unit.workPoint || ''}
          onChange={(e) => onWorkPointChange(e.target.value)}
          data-testid={`unit-work-point-${unitIndex}`}
        />
        <PlaceInput
          label='Пункт возврата'
          placeholder='Ангарск'
          value={unit.returnPoint || ''}
          onChange={(e) => onReturnPointChange(e.target.value)}
          data-testid={`unit-return-point-${unitIndex}`}
        />
      </FieldWrapper>

      <FieldWrapper>
        <PriceInput
          label='Стоимость доставки'
          placeholder='0'
          value={unit.deliveryCost?.toString() || ''}
          onChange={(e) =>
            onDeliveryCostChange(
              e.target.value ? Number(e.target.value) : undefined,
            )
          }
          type='money'
          data-testid={`unit-delivery-cost-${unitIndex}`}
        />
        <PriceInput
          label='Стоимость возврата'
          placeholder='0'
          value={unit.returnCost?.toString() || ''}
          onChange={(e) =>
            onReturnCostChange(
              e.target.value ? Number(e.target.value) : undefined,
            )
          }
          type='money'
          data-testid={`unit-return-cost-${unitIndex}`}
        />
      </FieldWrapper>

      <FieldWrapper>
        <HintTooltip label='Сколько единиц техники с такими же маршрутом, стоимостью доставки, сменами и навесным'>
          <QuantityInput
            label='Кол-во'
            placeholder='1'
            value={unit.quantity ?? 1}
            onChange={(e) => onQuantityChange(Number(e.target.value))}
            type='number'
            min={1}
            data-testid={`unit-quantity-${unitIndex}`}
          />
        </HintTooltip>
        <Select
          label='Кол-во смен'
          placeholder='Смены'
          value={unit.shiftCount?.toString() || '1'}
          onChange={onShiftCountChange}
          data={['1', '2']}
          defaultValue='1'
          data-testid={`unit-shifts-${unitIndex}`}
        />
      </FieldWrapper>

      <HintTooltip label='Навесное этой единицы: пишется в наименовании КП, стоимость его работы добавляется к ставке. Навесное по умолчанию (например, ковш) в наименовании не пишется'>
        <ToolInput
          label='Навесное'
          placeholder='Навесное'
          value={unit.toolId?.toString() || ''}
          onChange={onToolChange}
          data={tools}
          clearable
          data-testid={`unit-tool-${unitIndex}`}
        />
      </HintTooltip>

      {canDelete && (
        <DeleteButton
          onClick={onDelete}
          title='Удалить единицу'
          data-testid={`unit-delete-${unitIndex}`}
        >
          <IconX size={20} />
        </DeleteButton>
      )}
    </UnitContainer>
  )
}
